/**
 * GazeTracker — runs MediaPipe Face Landmarker (478 landmarks incl. iris,
 * WASM + GPU delegate, fully in-browser) against a webcam <video> element and
 * emits per-frame gaze features to a subscriber.
 *
 * Model + WASM are self-hosted (public/models/face_landmarker.task,
 * public/mediapipe/wasm/) following the same pattern as the face-api weights.
 */
import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision'
import { extractGazeFeatures, type GazeFeatures } from './gazeFeatures'
import type { CnnExtractor } from './cnnExtractor'

const WASM_BASE = '/mediapipe/wasm'
const MODEL_PATH = '/models/face_landmarker.task'

/**
 * Blendshape blink score above which a frame is treated as a blink. Kept high
 * on purpose: looking at the bottom of the screen droops the lids and raises
 * this score well past 0.4, and rejecting those frames starves the calibration
 * of low-y data. Real blinks score ~0.9; partial droop is genuine gaze signal
 * (openness is a model feature).
 */
const BLINK_THRESHOLD = 0.65

/**
 * The gaze container's blink rule (runtime/session.py @ v0.1.0): score above
 * 0.5, and the 100 ms after a closed frame still count as a blink. Used when
 * the CNN engine is on, so the lab drops exactly the frames the container drops.
 */
const CNN_BLINK_THRESHOLD = 0.5
const CNN_BLINK_SETTLE_MS = 100

export interface GazeFrame {
  /** performance.now() of the processed frame, ms. */
  t: number
  faceOk: boolean
  blink: boolean
  features: GazeFeatures | null
  /**
   * CNN feature row [pitch, yaw, hx, hy, hz] when the CNN engine is attached
   * and ran on this frame; null when it is off, found no usable pose, or was
   * still busy with an earlier frame (frames are skipped, never queued).
   */
  cnn: number[] | null
  /** Blink by the container's rule (see CNN_BLINK_THRESHOLD). */
  cnnBlink: boolean
  /** Processing rate over recent frames (EMA), Hz. */
  fps: number
}

export type GazeFrameHandler = (frame: GazeFrame) => void

export class GazeTracker {
  private landmarker: FaceLandmarker | null = null
  private video: HTMLVideoElement | null = null
  private raf = 0
  private running = false
  private disposed = false
  private lastVideoTime = -1
  private lastFrameT = 0
  private fps = 0
  private lastClosedT = Number.NEGATIVE_INFINITY
  onFrame: GazeFrameHandler | null = null
  /** Attach Miłosz's CNN; null runs the landmark features only. */
  cnn: CnnExtractor | null = null

  /** Loads WASM + model and binds the video element. Safe to call once. */
  async init(video: HTMLVideoElement): Promise<void> {
    const fileset = await FilesetResolver.forVisionTasks(WASM_BASE)
    const options = {
      runningMode: 'VIDEO' as const,
      numFaces: 1,
      outputFaceBlendshapes: true,
      outputFacialTransformationMatrixes: true,
    }
    try {
      this.landmarker = await FaceLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: MODEL_PATH, delegate: 'GPU' },
        ...options,
      })
    } catch {
      // Some machines lack usable WebGL for the GPU delegate — CPU still runs realtime.
      this.landmarker = await FaceLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: MODEL_PATH, delegate: 'CPU' },
        ...options,
      })
    }
    if (this.disposed) {
      // dispose() raced the async load — release the freshly created landmarker.
      this.landmarker.close()
      this.landmarker = null
      return
    }
    this.video = video
  }

  start(): void {
    if (this.running || !this.landmarker || !this.video) return
    this.running = true
    this.lastFrameT = performance.now()
    const loop = () => {
      if (!this.running) return
      this.processFrame()
      this.raf = requestAnimationFrame(loop)
    }
    this.raf = requestAnimationFrame(loop)
  }

  stop(): void {
    this.running = false
    cancelAnimationFrame(this.raf)
  }

  dispose(): void {
    this.stop()
    this.disposed = true
    this.landmarker?.close()
    this.landmarker = null
    this.video = null
    this.onFrame = null
  }

  private processFrame(): void {
    const video = this.video
    const landmarker = this.landmarker
    if (!video || !landmarker || video.readyState < 2) return
    if (video.currentTime === this.lastVideoTime) return // no new camera frame yet
    this.lastVideoTime = video.currentTime

    const t = performance.now()
    let result
    try {
      result = landmarker.detectForVideo(video, t)
    } catch {
      return // transient decode hiccup — skip the frame
    }

    const dt = (t - this.lastFrameT) / 1000
    this.lastFrameT = t
    if (dt > 0) this.fps = this.fps === 0 ? 1 / dt : this.fps * 0.9 + (1 / dt) * 0.1

    const features = extractGazeFeatures(result)
    if (features && features.blinkScore > CNN_BLINK_THRESHOLD) this.lastClosedT = t
    const frame: GazeFrame = {
      t,
      faceOk: features !== null,
      blink: features !== null && features.blinkScore > BLINK_THRESHOLD,
      features,
      cnn: null,
      cnnBlink: t - this.lastClosedT <= CNN_BLINK_SETTLE_MS,
      fps: this.fps,
    }

    const cnn = this.cnn
    const landmarks = result.faceLandmarks?.[0]
    if (!cnn?.ready || cnn.isBusy || !landmarks) {
      this.onFrame?.(frame)
      return
    }
    // extract() warps the video synchronously before its first await, so the
    // crop comes from the same camera frame the landmarks were detected on.
    void cnn.extract(video, video.videoWidth, video.videoHeight, landmarks).then((out) => {
      if (this.disposed) return
      this.onFrame?.({ ...frame, cnn: out?.features ?? null })
    })
  }
}
